import React, {useCallback, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import {CircularProgress, SvgIcon} from "@mui/material";
import {Edit} from "@mui/icons-material";
import classnames from "classnames";
import DraggableSwitch from "../../base/switch/draggable-switch";
import IconButton from "../../base/button/icon_button";
import LazyImage from "../lazy-image";
import DateUtils from "../../../../core/services/date-utils";
import {ReactComponent as ImagePlaceholder} from "../../../../assets/images/operator-profile-placeholder/operator-profile-placeholder-sidebar.svg";
import {ReactComponent as RemoveIcon} from "../../../../assets/images/remove.svg";
import CacheService from "../../../../core/services/cache/cache_service";
import useIsMounted from "../../../hooks/use-is-mounted";
import {useOperatorInformation} from "../../../hooks/use-operator-information";
import {setReduxDialogState} from "../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../core/models/static/routes/redux-store-routes";


const NotebookNoteCard = ({note, toggleIsPrivate, setNote, removeNote, className}) => {
    const [updating, setUpdating] = useState(false);
    const [removing, setRemoving] = useState(false);
    const dispatch = useDispatch();
    const isMounted = useIsMounted();
    const operatorInformation = useOperatorInformation();
    const {t} = useTranslation('components', {keyPrefix: 'app-specific.notebook-note-card'});

    const createdByProfileImageUrl = useMemo(() => CacheService.getOperatorProfileImage(note?.createdBy), [note?.createdBy]);
    const ownerOfNote = useMemo(() => note?.createdBy === operatorInformation?.id, [note?.createdBy, operatorInformation?.id]);

    /**
     * Toggles the note's isPrivate property
     * @param {boolean} value
     */
    const toggleNoteIsPrivate = async (value) => {
        setUpdating(true);
        await toggleIsPrivate(note, !value);
        if (!isMounted()) {
            return;
        }
        setUpdating(false);
    };

    /**
     * Opens the update note dialog
     */
    const openUpdateNoteDialog = () => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.upsertNote, {
            open: true,
            note: note,
            onSuccess: (updatedNote) => setNote(note.id, updatedNote),
        }));
    };

    /**
     * Confirms the removal of the note
     */
    const confirmRemoval = useCallback(() => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    {t('removeConfirmation')}
                </p>
            </div>,
            {},
            async (decision) => {
                if (!decision) {
                    return;
                }
                setRemoving(true);
                await removeNote(note?.id);
                if (!isMounted()) {
                    return;
                }
                setRemoving(false);
            });
    }, [isMounted, note?.id, removeNote, t]);

    return (
        <div className={classnames('note-card', className)}>
            <div className={'note-title'}>
                <div className={'title'}>
                    <p>
                        {note?.title}
                    </p>
                </div>
                <div className={'d-flex align-items-center justify-content-end ml-auto'}>
                    {
                        ownerOfNote &&
                        <DraggableSwitch
                            activeTitle={t('public')}
                            inActiveTitle={t('private')}
                            size={4}
                            disabled={updating}
                            checked={!note?.isPrivate}
                            onChange={(checked) => toggleNoteIsPrivate(checked)}
                        />
                    }
                    {
                        ownerOfNote &&
                        <IconButton
                            disabled={updating}
                            onPressed={openUpdateNoteDialog}
                            className={'mx-1'}
                            color={'paper.secondary'}
                        >
                            {
                                updating
                                    ? <CircularProgress size={20} color={'secondary'}/>
                                    : <Edit className='icon small'/>
                            }
                        </IconButton>
                    }
                    {
                        ownerOfNote &&
                        <IconButton
                            disabled={removing}
                            onPressed={confirmRemoval}
                            color={'paper.error'}
                        >
                            {
                                removing
                                    ? <CircularProgress size={20} color={'secondary'}/>
                                    : <SvgIcon component={RemoveIcon} className='icon normal'/>
                            }
                        </IconButton>
                    }
                </div>
            </div>
            <div className={'note-info'}>
                <div
                    className={'note-content'}
                    dangerouslySetInnerHTML={{__html: note?.body ?? ""}}
                />
                <div className={classnames('assigned-container mr-auto', {'self-assigned': ownerOfNote})}>
                    <div className={'assigner-image-container'}>
                        <LazyImage
                            alt={t('assignerImageAlt')}
                            noImage={<ImagePlaceholder/>}
                            src={createdByProfileImageUrl}
                            height={27}
                        />
                    </div>
                    <p className={'assigner-text'}>
                        {t('assignedBy')}{' '}
                        <span className={'assigner-name'}>{note?.createdByName ?? ''}</span>
                    </p>
                </div>
                <div className={'date-container ml-auto'}>
                    {
                        note?.modifiedDateTime &&
                        <p className={'date'}>
                            {t('lastModifiedAt')}
                            <span className={'date-inner'}>
                                {DateUtils.serverToLocal(note?.modifiedDateTime).format(DateUtils.dateTimeFormat)}
                            </span>
                        </p>
                    }
                </div>
            </div>
        </div>
    );
};

export default NotebookNoteCard;
